import type { RootStore } from '.';

/**
 * Session integrity utilities to detect and repair data corruption issues
 * particularly around card deletion operations
 */
export class SessionIntegrityUtil {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  /**
   * Check for orphaned card IDs in session.cardIds that don't exist in Firestore
   * This is the main cause of crashes during card operations
   */
  async checkForOrphanedCardIds(): Promise<{
    orphanedIds: string[];
    hasIssues: boolean;
  }> {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) {
      return { orphanedIds: [], hasIssues: false };
    }

    const sessionCardIds = currentSession.data.cardIds || [];
    const activeCards = this.rootStore.cardsStore.cards;
    const { deletedCards } = this.rootStore.deletedCardsStore;

    // Find card IDs that exist in session.cardIds but not in either store
    const orphanedIds = sessionCardIds.filter(
      (cardId) => !activeCards[cardId] && !deletedCards[cardId],
    );

    return {
      orphanedIds,
      hasIssues: orphanedIds.length > 0,
    };
  }

  /**
   * Automatically repair orphaned card IDs by removing them from session.cardIds
   */
  async repairOrphanedCardIds(): Promise<boolean> {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return false;

    const { orphanedIds, hasIssues } = await this.checkForOrphanedCardIds();

    if (!hasIssues) return true;

    try {
      console.warn(`🔧 Repairing ${orphanedIds.length} orphaned card IDs:`, orphanedIds);

      // Remove orphaned IDs from session
      const cleanedCardIds = currentSession.data.cardIds.filter(
        (cardId) => !orphanedIds.includes(cardId),
      );

      await currentSession.sessionRef.set({ cardIds: cleanedCardIds }, { merge: true });

      console.log(
        `✅ Successfully repaired session integrity. Removed ${orphanedIds.length} orphaned card IDs.`,
      );
      return true;
    } catch (error) {
      console.error('❌ Failed to repair orphaned card IDs:', error);
      return false;
    }
  }

  /**
   * Check for duplicate card IDs in session.cardIds array
   */
  checkForDuplicateCardIds(): {
    duplicates: string[];
    hasIssues: boolean;
  } {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) {
      return { duplicates: [], hasIssues: false };
    }

    const cardIds = currentSession.data.cardIds || [];
    const seen = new Set<string>();
    const duplicates: string[] = [];

    for (const cardId of cardIds) {
      if (seen.has(cardId)) {
        duplicates.push(cardId);
      } else {
        seen.add(cardId);
      }
    }

    return {
      duplicates,
      hasIssues: duplicates.length > 0,
    };
  }

  /**
   * Repair duplicate card IDs by deduplicating the array
   */
  async repairDuplicateCardIds(): Promise<boolean> {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return false;

    const { duplicates, hasIssues } = this.checkForDuplicateCardIds();

    if (!hasIssues) return true;

    try {
      console.warn(`🔧 Repairing ${duplicates.length} duplicate card IDs:`, duplicates);

      // Deduplicate the array while preserving order
      const deduplicatedCardIds = [...new Set(currentSession.data.cardIds)];

      await currentSession.sessionRef.set({ cardIds: deduplicatedCardIds }, { merge: true });

      console.log(
        `✅ Successfully repaired duplicate card IDs. Removed ${
          currentSession.data.cardIds.length - deduplicatedCardIds.length
        } duplicates.`,
      );
      return true;
    } catch (error) {
      console.error('❌ Failed to repair duplicate card IDs:', error);
      return false;
    }
  }

  /**
   * Run a comprehensive health check and repair common issues
   */
  async runHealthCheck(): Promise<{
    issues: string[];
    repaired: boolean;
  }> {
    const issues: string[] = [];
    let allRepaired = true;

    // Check for orphaned card IDs
    const orphanCheck = await this.checkForOrphanedCardIds();
    if (orphanCheck.hasIssues) {
      issues.push(`Found ${orphanCheck.orphanedIds.length} orphaned card IDs`);
      const repaired = await this.repairOrphanedCardIds();
      if (!repaired) allRepaired = false;
    }

    // Check for duplicate card IDs
    const duplicateCheck = this.checkForDuplicateCardIds();
    if (duplicateCheck.hasIssues) {
      issues.push(`Found ${duplicateCheck.duplicates.length} duplicate card IDs`);
      const repaired = await this.repairDuplicateCardIds();
      if (!repaired) allRepaired = false;
    }

    if (issues.length === 0) {
      console.log('✅ Session integrity check passed - no issues found');
    }

    return {
      issues,
      repaired: allRepaired,
    };
  }
}
